import React, { useState, useEffect } from 'react';
import toast from 'react-hot-toast';
import { devicesAPI, companiesAPI, depotsAPI } from '../services/api';
import DataTable from '../components/DataTable';
import { useAuthStore } from '../store/authStore';
import { formatDateForTimezone } from '../utils/datetime';

export default function DevicesView() {
  const { user: currentUser } = useAuthStore();
  const timezone = useAuthStore((state) => state.timezone);
  const [devices, setDevices] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [depots, setDepots] = useState([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    fetchDevices();
    fetchCompanies();
    fetchDepots();
  }, []);

  const fetchDevices = async () => {
    setLoading(true);
    try {
      // Use the main endpoint - backend handles filtering by company/depot for non-gods_eye users
      const response = await devicesAPI.getAll();
      const deviceList = response.data || [];
      setDevices(deviceList);
    } catch (error) {
      console.error('Error fetching seals:', error);
      console.error('Error response:', error.response);
      toast.error('Error fetching seals: ' + (error.response?.data?.detail || error.message));
      setDevices([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchCompanies = async () => {
    try {
      const response = await companiesAPI.getAll();
      setCompanies(response.data || []);
    } catch (error) {
      // Silently handle 403 errors (user doesn't have access)
      if (error.response?.status !== 403) {
        console.error('Error fetching companies:', error);
      }
    }
  };

  const fetchDepots = async () => {
    try {
      const response = await depotsAPI.getAll();
      setDepots(response.data || []);
    } catch (error) {
      // Silently handle 403 errors (user doesn't have access)
      if (error.response?.status !== 403) {
        console.error('Error fetching depots:', error);
      }
    }
  };

  const getCompanyName = (companyId) => {
    if (!companyId) return 'Unassigned';
    const company = companies.find(c => c.id === companyId);
    return company ? company.name : `Company #${companyId}`;
  };

  const getDepotName = (depotId) => {
    if (!depotId) return '-';
    const depot = depots.find(d => d.id === depotId);
    return depot ? depot.name : `Depot #${depotId}`;
  };

  const columns = [
    { key: 'id', label: 'ID', width: '80px', sortable: true },
    { key: 'box', label: 'Box', sortable: true },
    ...(currentUser?.user_type !== 'distribuidor' ? [
      { key: 'bag', label: 'Bag', sortable: true }
    ] : []),
    ...(currentUser?.user_type !== 'operador' ? [
      { key: 'seal', label: 'Seal', sortable: true }
    ] : []),
    { key: 'container_number', label: 'Container #', sortable: true },
    {
      key: 'container_assigned_by',
      label: 'Assigned By',
      render: (value) => value?.username || '-',
      sortable: true
    },
    {
      key: 'container_assigned_at',
      label: 'Assigned At',
      render: (value) =>
        value
          ? formatDateForTimezone(value, timezone, 'es-ES', {
              year: 'numeric',
              month: '2-digit',
              day: '2-digit',
              hour: '2-digit',
              minute: '2-digit',
            })
          : '-',
      sortable: true
    },
    ...(currentUser?.user_type !== 'operador'
      ? [
          // Hide company column for cliente users; they only work in their own company
          ...(currentUser?.user_type !== 'cliente'
            ? [{
                key: 'company_id',
                label: 'Company',
                render: (value) => getCompanyName(value),
                sortable: true,
              }]
            : []),
          {
            key: 'depot_id',
            label: 'Depot',
            render: (value) => getDepotName(value),
            sortable: true,
          },
          {
            key: 'created_at',
            label: 'Created Date',
            render: (value) => formatDateForTimezone(value, timezone),
            sortable: true,
          },
        ]
      : []),
  ];

  return (
    <div className="container">
      <h1 className="page-title">Seals</h1>

      {loading ? (
        <p>Loading seals...</p>
      ) : devices.length === 0 ? (
        <p>No seals available.</p>
      ) : (
        <DataTable
          data={devices}
          columns={columns}
          searchableFields={['box', 'bag', 'seal']}
          itemsPerPage={10}
          title="Seals"
        />
      )}
    </div>
  );
}
